import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { AppConfig, Theme } from '@h2/types';

interface AppState {
  // Theme
  theme: Theme;
  setTheme: (theme: Theme) => void;
  
  // Loading states
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  
  // Notifications
  notifications: boolean;
  setNotifications: (enabled: boolean) => void;
  
  // Language
  language: string;
  setLanguage: (language: string) => void;
  
  // Config
  config: AppConfig;
  updateConfig: (config: Partial<AppConfig>) => void;
  
  // Reset
  reset: () => void;
}

const defaultConfig: AppConfig = {
  theme: 'system',
  language: 'ko',
  notifications: true,
};

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set) => ({
        // Theme
        theme: 'system',
        setTheme: (theme) => set({ theme }),
        
        // Loading states
        isLoading: false,
        setLoading: (isLoading) => set({ isLoading }),
        
        // Notifications
        notifications: true,
        setNotifications: (notifications) => set({ notifications }),
        
        // Language
        language: 'ko',
        setLanguage: (language) => set({ language }),
        
        // Config
        config: defaultConfig,
        updateConfig: (newConfig) => 
          set((state) => ({
            config: { ...state.config, ...newConfig }
          })),
        
        // Reset
        reset: () => set({
          theme: 'system',
          isLoading: false,
          notifications: true,
          language: 'ko',
          config: defaultConfig,
        }),
      }),
      {
        name: 'app-store',
        partialize: (state) => ({
          theme: state.theme,
          notifications: state.notifications,
          language: state.language,
          config: state.config,
        }),
      }
    ),
    {
      name: 'app-store',
    }
  )
);
